import { createClient, drawingSrcGetter } from "@local/lamina-core";
import chunk from "lodash/chunk";
import { alerts } from "#src/stores/alerts.js";
import { Sentry } from "#src/glitchtip.js";
import {
  COOKIE_DOMAIN as cookieDomain,
  SUPABASE_URL as supabaseUrl,
  TRIOPS_URL as triopsUrl,
  SUPABASE_ANON_KEY as supabaseAnonKey,
  BACKEND_PREFIX as backendPrefix,
  WEBSOCKET_URL,
} from "#src/env.js";

// Run `yarn db:gen:types` to update type definitions.
const api = createClient(supabaseUrl, supabaseAnonKey, {
  cookies: {
    domain: cookieDomain,
  },
});

// If server restarts, force reload to ensure that user has latest version.
let serverID;
export const ws = new WebSocket(WEBSOCKET_URL);
function connect() {
  ws.onmessage = (e) => {
    const { appID } = JSON.parse(e.data);
    console.log("[app] Connection with server:", appID);
    if (!serverID) {
      serverID = appID;
    } else if (serverID !== appID) {
      location.reload();
    }
  };

  ws.onclose = (e) => {
    console.log(
      "Connection with server is closed. Reconnect will be attempted in 1 seconds.",
    );
    setTimeout(function () {
      connect();
    }, 2000);
  };

  ws.onerror = (err) => {
    ws.close();
  };
}
connect();

function jsonHeaders() {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

async function jsonResult(res) {
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return res.json();
  }

  if (res.ok) {
    return { status: res.status, message: res.statusText };
  }

  return { error: { status: res.status, message: res.statusText } };
}

async function updateProp(table, id, prop, value) {
  try {
    const update = typeof prop === "object" ? { ...prop } : { [prop]: value };
    if (Array.isArray(id)) {
      // Break into chunks of < 50 to avoid URI length limits.
      const promises = chunk(id, 50).map(async (ids) => {
        const { data, error } = await api
          .from(table)
          .update(update)
          .in("id", ids);

        if (error) throw error;
        return data;
      });

      return Promise.all(promises);
    } else {
      const updated = await api
        .from(table)
        .update(update)
        .eq("id", id)
        .throwOnError();

      if (updated.error) throw updated.error;
      return updated.data;
    }
  } catch (error) {
    const ids = Array.isArray(id) ? id.join(", ") : id;
    const err = new Error(
      `Supabase api error updating table: ${table}, id: ${ids}, prop: ${JSON.stringify(prop)}`,
      { cause: error },
    );
    alerts.add({
      type: "danger",
      message: `Error persisting data to table ${table}.`,
    });
    Sentry.captureException(err);
    console.log(error);
  }
}

async function addRecords(table, records) {
  try {
    const { data, error } = await api.from(table).insert(records);

    if (error) throw error;

    return data;
  } catch (error) {
    const err = new Error(`Supabase api error inserting into: ${table}`, {
      cause: error,
    });
    alerts.add({
      type: "danger",
      message: `Error adding records to table ${table}.`,
    });
    Sentry.captureException(err);
    console.log(err);
  }
}

async function removeRecords(table, ids) {
  try {
    const promises = chunk(ids, 50).map(async (chunkedIds) => {
      const { data, error } = await api
        .from(table)
        .delete()
        .in("id", chunkedIds);

      if (error) throw error;
      return data;
    });

    return (await Promise.all(promises)).flat();
  } catch (error) {
    const err = new Error(`Supabase api error deleting from: ${table}`, {
      cause: error,
    });
    alerts.add({
      type: "danger",
      message: `Error removing data from table ${table}.`,
    });
    Sentry.captureException(err);
    console.log(err);
  }
}

async function checkBackendHealth() {
  const res = await fetch(`${backendPrefix}/healthz`);
  if (res.ok) {
    return res.text();
  } else {
    return null;
  }
}

async function requestDemo(email) {
  const res = await fetch(`${backendPrefix}/request-demo`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ email, context: "app" }),
  });

  return res;
}

async function addUser(user) {
  const res = await fetch(`${backendPrefix}/users`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ user }),
  });

  return res.json();
}

async function signUp(signup) {
  const res = await fetch(`${backendPrefix}/signup`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(signup),
  });

  return jsonResult(res);
}

async function addContact(contact) {
  const res = await fetch(`${backendPrefix}/contacts`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(contact),
  });

  return jsonResult(res);
}

async function addCustomer(customer) {
  const res = await fetch(`${backendPrefix}/customers`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(customer),
  });

  return jsonResult(res);
}

async function addRep(rep) {
  const res = await fetch(`${backendPrefix}/reps`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(rep),
  });

  return jsonResult(res);
}

async function resendCustomerInvitation(customer) {
  const res = await fetch(`${backendPrefix}/customers/resend-invitation`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(customer),
  });

  return jsonResult(res);
}

async function resendRepInvitation(rep) {
  const res = await fetch(`${backendPrefix}/reps/resend-invitation`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(rep),
  });

  return jsonResult(res);
}

async function addGuest(guest) {
  const res = await fetch(`${backendPrefix}/guests`, {
    method: "POST",
    body: JSON.stringify(guest),
    headers: jsonHeaders(),
  });

  return jsonResult(res);
}

async function sendMagicLink(id) {
  fetch(`${backendPrefix}/magiclink`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ id }),
  });
}

async function signIn({ email, redirectTo }) {
  const res = await fetch(`${backendPrefix}/signin`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ email, redirectTo }),
  });

  return jsonResult(res);
}

async function getGroupFromLink({ link_id }) {
  const res = await fetch(`${backendPrefix}/links/group`, {
    method: "POST",
    body: JSON.stringify({ link_id }),
    headers: jsonHeaders(),
  });

  return res.json();
}

async function getPasswordResetLink({ email, redirectTo }) {
  const res = await fetch(`${backendPrefix}/resetpassword`, {
    method: "POST",
    body: JSON.stringify({ email, redirectTo }),
    headers: jsonHeaders(),
  });

  return res.json();
}

async function setUserPassword({ id, password }) {
  const res = await fetch(`${backendPrefix}/resetpassword/set`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ id, password }),
  });

  return jsonResult(res);
}

async function storeObject(group, bucket, object, contentType, file) {
  const res = await fetch(
    `${backendPrefix}/storage/object/${group}/${bucket}/${encodeURIComponent(object)}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": contentType,
      },
      body: file,
    },
  );

  return jsonResult(res);
}

async function storeDrawing(object, contentType, file) {
  const res = await fetch(
    `${backendPrefix}/storage/store-drawing/${encodeURIComponent(object)}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": contentType,
      },
      body: file,
    },
  );

  return jsonResult(res);
}

const drawingSrc = drawingSrcGetter(supabaseUrl, triopsUrl);

async function checkContact(request) {
  const res = await fetch(`${backendPrefix}/contacts/check`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });

  return jsonResult(res);
}

async function sendInvitation(request) {
  const res = await fetch(`${backendPrefix}/invitations/`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });

  return jsonResult(res);
}

async function useInvitation(request) {
  const res = await fetch(`${backendPrefix}/invitations/use`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });

  return jsonResult(res);
}

async function resendInvitation(request) {
  const res = await fetch(`${backendPrefix}/invitations/resend`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });

  return jsonResult(res);
}

async function acceptInvitation(email) {
  const res = await fetch(
    `${backendPrefix}/invitations/accept?email=${encodeURIComponent(email)}`,
    {
      method: "GET",
      headers: jsonHeaders(),
    },
  );

  return jsonResult(res);
}

async function reloadClients() {
  const res = await fetch(`${backendPrefix}/appid`, {
    method: "POST",
    headers: jsonHeaders(),
  });

  return jsonResult(res);
}

/**
 * @param {string} [orgId]
 */
async function getSupportVideos(orgId) {
  let url = `${backendPrefix}/support/videos`;

  if (typeof orgId === "string") {
    url = `${url}/${orgId}`;
  }

  const res = await fetch(url, {
    method: "GET",
    headers: jsonHeaders(),
  });

  return jsonResult(res);
}

async function getSupportVideo(videoUrl) {
  const res = await fetch(`${backendPrefix}/support/video`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ url: videoUrl }),
  });

  return jsonResult(res);
}

async function getDocPdf(docId) {
  const res = await fetch(`${backendPrefix}/documents/create/${docId}`, {
    method: "GET",
    headers: jsonHeaders(),
  });

  return jsonResult(res);
}

async function doesOrgAllowTallyRequest(orgId) {
  const res = await fetch(
    `${backendPrefix}/organizations/${orgId}/allows-tally-request`,
    {
      method: "GET",
      headers: jsonHeaders(),
    },
  );

  const result = await jsonResult(res);
  return result?.allows ?? false;
}

async function submitTallyRequest(request) {
  const res = await fetch(`${backendPrefix}/tally-requests`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ request }),
  });

  return jsonResult(res);
}

async function submitShpxRequest(request) {
  const res = await fetch(`${backendPrefix}/tally-requests/shpx`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ request }),
  });

  return jsonResult(res);
}

async function createNotionCustomer(organization_id) {
  const res = await fetch(
    `${backendPrefix}/tally-requests/create-notion-customer`,
    {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({ organization_id }),
    },
  );

  return jsonResult(res);
}

async function prioritizePages({ pages, name, priority = 1 }) {
  const res = await fetch(`${backendPrefix}/queue/prioritize-pages`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({ pages, name, priority }),
  });

  return jsonResult(res);
}

export {
  api,
  jsonResult,
  jsonHeaders,
  updateProp,
  addRecords,
  removeRecords,
  requestDemo,
  addUser,
  addContact,
  addCustomer,
  addRep,
  resendCustomerInvitation,
  resendRepInvitation,
  addGuest,
  checkContact,
  sendMagicLink,
  getPasswordResetLink,
  setUserPassword,
  signIn,
  signUp,
  getGroupFromLink,
  storeObject,
  storeDrawing,
  checkBackendHealth,
  drawingSrc,
  sendInvitation,
  useInvitation,
  resendInvitation,
  acceptInvitation,
  reloadClients,
  getSupportVideos,
  getSupportVideo,
  getDocPdf,
  doesOrgAllowTallyRequest,
  submitTallyRequest,
  submitShpxRequest,
  createNotionCustomer,
  prioritizePages,
};
